import WebReturnsSidebar from '../../components/web-returns/web-returns-sidebar';
import SearchForm from '../../model/search-form';
import Table from '../../components/shared/table';
import MessageHelper from '../../helpers/message-helper';
import HTMLTemplateHelper from '../../helpers/html-template-helper';
import SalesOrderData from '../../model/sales-order-data';
import {
  formatCurrency, formatDate, formatNumber, getParameterByName,
} from '../../helpers/utils';
import AbstractAsyncDetailTablePage from '../abstract-async-detail-table-page';
import SalesOrderEntryData from '../../model/sales-order-entry-data';

export default class WebReturnsListPage extends AbstractAsyncDetailTablePage<SalesOrderData> {
  protected readonly searchForm: SearchForm;
  protected readonly table: Table;
  private readonly totalAmount: JQuery;

  protected readonly tableColumnDefinitions: DataTables.ColumnDefsSettings[] = [
    {
      className: 'js-showDetails',
      targets: 0,
      orderable: false,
      searchable: false,
      data: null,
      defaultContent: '<i class="table__toggle-child fas fa-chevron-right"></i>',
    }, {
      targets: 1,
      data: (data: SalesOrderData): string => `<a>${data.orderId}</a>`,
      className: 'cursor-pointer js-showDetails',
    },
    { targets: 2, data: 'externalId' },
    { targets: 3, data: 'status' },
    {
      targets: 4,
      data: (data: SalesOrderData, type: string): string | number => {
        if (type === 'sort') {
          return data.voucherDate;
        }

        return data.orderDate;
      },
    },
    { targets: 5, data: ({ netAmount }: SalesOrderData): string => formatNumber(netAmount, 2), className: 'text-align-right' },
    { targets: 6, data: ({ grossAmount }: SalesOrderData): string => formatNumber(grossAmount, 2), className: 'text-bold text-align-right' },
    {
      targets: 7,
      data: (data: SalesOrderData): string => HTMLTemplateHelper.createLinkButton({
        buttonClass: '-clear -compact',
        href: `${window.ACC.config.encodedContextPath}/webreturns/print/history?token=${data.fileDownloadJwt}`,
        text: MessageHelper.getMessage('common.print'),
        textClass: 'sr-only',
        icon: 'fas fa-file-download',
      }).outerHTML,
      className: 'text-align-center',
    },
  ];

  protected detailTableTemplate: JQuery<HTMLTemplateElement>;
  protected detailTableColumnDefinitions: DataTables.ColumnDefsSettings[] = [
    { targets: 0, data: 'id', width: '5%' },
    { targets: 1, data: 'materialNumber', width: '15%' },
    {
      targets: 2, data: 'orderQuantity', width: '5%', className: 'text-align-right',
    },
    { targets: 3, data: 'materialDescription', width: '20%' },
    {
      targets: 4, data: ({ netValue }: SalesOrderEntryData): string => formatNumber(netValue, 2), width: '15%', className: 'text-align-right',
    },
    { targets: 5, data: ({ status }: SalesOrderEntryData): string => MessageHelper.getMessage(status), width: '10%' },
    {
      targets: 6, data: 'openedQuantity', width: '10%', className: 'text-align-right',
    },
    { targets: 7, data: 'referenceOrderId', width: '10%' },
    {
      targets: 8,
      data: ({ deliveryShippingDate }: SalesOrderEntryData, type: string): string | number => {
        if (type === 'sort') {
          if (deliveryShippingDate === null) {
            return 0;
          }
          return deliveryShippingDate;
        }
        return deliveryShippingDate ? formatDate(new Date(deliveryShippingDate)) : '';
      },
      width: '10%',
    },
  ];

  constructor(private readonly body: JQuery) {
    super();

    this.searchForm = new WebReturnsSidebar(this.body.find('.js-searchForm') as JQuery<HTMLFormElement>);
    this.detailTableTemplate = body.find<HTMLTemplateElement>('#js-returnDetailTable');
    this.table = new Table(body.find('.js-table'), this.tableColumnDefinitions, {
      drawCallback: (settings: DataTables.SettingsLegacy) => {
        const datatable = Table.getInstance(settings);
        const searchTerm = getParameterByName('searchOption');
        const selectedId = getParameterByName('searchTerm');
        if (searchTerm === 'ID' && selectedId) {
          const row = datatable.row(0);
          if (row.data() && !row.child.isShown()) {
            this.toggleDetails($(row.node() as HTMLTableRowElement), true);
          }
        }
      },
    });
    this.totalAmount = body.find('.js-tableSumValue');

    this.bindAsyncData();
    this.bindShowDetails();
  }

  protected setRows(data: SalesOrderData[]): void {
    super.setRows(data);
    const total = data.reduce((sum, salesOrder) => sum + salesOrder.netAmount ?? 0, 0);
    this.totalAmount.text(formatCurrency(total));
  }

  // eslint-disable-next-line class-methods-use-this
  protected getDetailUrl(parent: SalesOrderData): string {
    return `${window.ACC.config.encodedContextPath}/webreturns/history/details/${parent.orderId}`;
  }
}
